import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Divider,
} from '@mui/material';

type HistoryData = {
    id: number;
    action: string;
    data: Record<string, any>;
    created_at: string;
    user: {
        name: string;
    };
};

type Props = {
    open: boolean;
    onClose: () => void;
    history: HistoryData[];
    title: string;
    fieldLabels?: Record<string, string>;
    valueMaps?: Record<string, Record<string, string>>;
    hiddenFields?: string[];
};

export const HistoryModal = ({ open, onClose, history, fieldLabels, valueMaps, hiddenFields, title }: Props) => {

    const formatValue = (key: string, value: any): string => {
        let displayValue = value;
        if ((key === "weight" || key === "category") && typeof value === "string") {
            displayValue = value.split(";")[0];
        }
        if (valueMaps?.[key] && valueMaps[key][String(displayValue)]) {
            displayValue = valueMaps[key][String(displayValue)];
        }
        if (typeof displayValue === "boolean") {
            return displayValue ? "✅ Sim" : "❌ Não";
        }
        if (displayValue === "true") return "✅ Sim";
        if (displayValue === "false") return "❌ Não";
        return typeof displayValue === "string"
            ? displayValue.toUpperCase()
            : String(displayValue ?? "");
    };

    const renderEntry = (entry: HistoryData) => {
        const entries = Object.entries(entry.data).filter(
            ([key]) => !hiddenFields?.includes(key)
        );

        // Detectar formato snapshot (tem campo "alterado")
        const hasSnapshotFormat = entries.some(
            ([, value]) => value && typeof value === "object" && "alterado" in value
        );

        if (hasSnapshotFormat) {
            return entries.map(([key, value]) => {
                const label = fieldLabels?.[key] || key;

                if (value?.alterado) {
                    // Campo alterado: mostra de → para
                    const de = formatValue(key, value.de);
                    const para = formatValue(key, value.para);
                    return (
                        <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                            <strong>{label.toUpperCase()}:</strong>{" "}
                            <span style={{ color: "#b71c1c", textDecoration: "line-through" }}>{de || "(vazio)"}</span>
                            {" → "}
                            <span style={{ color: "#2e7d32", fontWeight: "bold" }}>{para}</span>
                        </Typography>
                    );
                }

                // Campo não alterado: mostra valor atual
                const formatted = formatValue(key, value.valor);
                return (
                    <Typography key={key} variant="body2" sx={{ mb: 0.5, color: "#757575" }}>
                        <strong>{label.toUpperCase()}:</strong> {formatted}
                    </Typography>
                );
            });
        }

        // Detectar formato diff simples (tem "de" e "para" sem "alterado")
        const hasDiffFormat = entries.some(
            ([, value]) => value && typeof value === "object" && "de" in value && "para" in value
        );

        if (hasDiffFormat) {
            return entries.map(([key, value]) => {
                const label = fieldLabels?.[key] || key;
                if (value && typeof value === "object" && "de" in value && "para" in value) {
                    const de = formatValue(key, value.de);
                    const para = formatValue(key, value.para);
                    return (
                        <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                            <strong>{label.toUpperCase()}:</strong>{" "}
                            <span style={{ color: "#b71c1c", textDecoration: "line-through" }}>{de || "(vazio)"}</span>
                            {" → "}
                            <span style={{ color: "#2e7d32", fontWeight: "bold" }}>{para}</span>
                        </Typography>
                    );
                }
                const formatted = formatValue(key, value);
                return (
                    <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                        <strong>{label.toUpperCase()}:</strong> {formatted}
                    </Typography>
                );
            });
        }

        // Formato legado: apenas valores
        return entries.map(([key, value]) => {
            const label = fieldLabels?.[key] || key;
            const formatted = formatValue(key, value);
            return (
                <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                    <strong>{label.toUpperCase()}:</strong> {formatted}
                </Typography>
            );
        });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>Histórico de Alterações - {title}</DialogTitle>
            <DialogContent dividers>
                {history.length === 0 ? (
                    <Typography>Nenhuma alteração registrada.</Typography>
                ) : (
                    history.map((entry) => (
                        <Box key={entry.id} mb={2}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {entry.action === 'CREATE' ? `${title} Criada` : `${title} Atualizada`} em: {new Date(entry.created_at).toLocaleString()}
                                <br />
                                {entry.action === 'CREATE' ? 'Criado por:' : 'Modificado por:'} {entry.user.name}
                            </Typography>
                            <Box mt={1}>
                                {renderEntry(entry)}
                            </Box>
                            <Divider sx={{ my: 1 }} />
                        </Box>
                    ))
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ backgroundColor: '#373737', color: 'white', fontFamily: 'inter, sans-serif' }}>
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
};
